import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export const GRACE_PRESETS = [[0, "Ban immediately"], [1, "1 hour"], [12, "12 hours"], [24, "24 hours"], [48, "48 hours"], [72, "72 hours"], [168, "7 days"]];

export const graceLabel = (h) => {
  if (!h) return "Immediate ban";
  if (h % 24 === 0) return `${h / 24} day${h === 24 ? "" : "s"}`;
  return `${h} hour${h === 1 ? "" : "s"}`;
};

// Preset dropdown + optional custom hours (0–720) for a blacklisted server's grace period.
export function GracePicker({ value, onChange, testId, compact = false }) {
  const [custom, setCustom] = useState(!GRACE_PRESETS.some(([h]) => h === value));
  useEffect(() => { if (!GRACE_PRESETS.some(([h]) => h === value)) setCustom(true); }, [value]);
  return (
    <div className="flex items-center gap-2">
      <select
        value={custom ? "custom" : String(value)}
        onChange={(e) => {
          if (e.target.value === "custom") { setCustom(true); return; }
          setCustom(false); onChange(Number(e.target.value));
        }}
        className={`${compact ? "px-2 py-1 text-xs" : "px-3 py-2.5 text-sm"} w-full`}
        data-testid={testId}
      >
        {GRACE_PRESETS.map(([h, l]) => <option key={h} value={h}>{compact ? graceLabel(h) : `Time to leave: ${l}`}</option>)}
        <option value="custom">Custom hours…</option>
      </select>
      {custom && (
        <input
          type="number" min={0} max={720} value={value}
          onChange={(e) => onChange(Math.max(0, Math.min(720, parseInt(e.target.value || "0", 10))))}
          className={`${compact ? "px-2 py-1 text-xs" : "px-3 py-2.5 text-sm"} w-20`}
          data-testid={`${testId}-custom`}
          aria-label="Custom hours"
        />
      )}
    </div>
  );
}

// Inline editor in the blacklist table; changing it moves every open countdown's deadline.
export function GraceCell({ entry, onSaved }) {
  const [val, setVal] = useState(entry.grace_hours || 0);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/blacklist/${entry.guild_id}`, { grace_hours: val });
      toast.success(`Time to leave → ${graceLabel(val)}${data.updated ? ` · ${data.updated} countdown(s) updated, ${data.notified} being DM'd` : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 min-w-[11rem]">
      <GracePicker value={val} onChange={setVal} testId={`blacklist-grace-${entry.guild_id}`} compact />
      {val !== (entry.grace_hours || 0) && (
        <button onClick={save} disabled={saving} className="text-emerald-400 hover:text-white" title="Save" data-testid={`blacklist-grace-save-${entry.guild_id}`}><Save size={14} /></button>
      )}
      {entry.open_countdowns > 0 && <span className="text-[0.65rem] font-mono-ui text-amber-300 whitespace-nowrap" data-testid={`blacklist-open-${entry.guild_id}`}>{entry.open_countdowns} counting</span>}
    </div>
  );
}
